import {
  // Category defaults
  CreditCard, Briefcase, ShoppingBag, Sparkles, Heart, Monitor, Megaphone, TrendingUp,
  // Credit & finance
  Shield, ShieldCheck, ShieldAlert, FileText, Landmark, Wallet, PiggyBank, Receipt,
  Percent, BadgeDollarSign, DollarSign, Coins, LineChart, BarChart3, Calculator,
  Home, Car, GraduationCap, Users2, ScrollText, Handshake, Lock, Key,
  // Business & compliance
  Building2, Hash, Signature, Stamp, ClipboardList, ClipboardCheck, Boxes, MailPlus,
  Phone, Building, Warehouse, MapPin, Contact, PenLine, FileSignature, Umbrella,
  BookOpen, BadgeCheck, Copyright, Scale, HandCoins,
  // E-commerce & online
  ShoppingCart, Package, Store, Palette, Music, Youtube, Instagram, Facebook, Twitter,
  Truck, RefreshCcw, MessagesSquare, MessageSquare, MessageCircle,
  Bell, BellRing, Repeat, Star, Layers, ScanBarcode, QrCode,
  Shirt,
  // Brand & design
  Type, Camera, Video, Image, Wand2, Feather,
  Paintbrush, PenTool, ImagePlus, Aperture, Gift, Bookmark, Ticket, PartyPopper,
  Newspaper, Users, UserPlus, Award, Trophy, Globe2, Leaf, Rocket,
  // Nonprofit
  HeartHandshake, HandHeart, Church, Sprout, HelpingHand, Flag, BookMarked,
  Vote, Group, School, Route,
  // Websites & tech
  Globe, Smartphone, Code2, Cpu, Bot, Zap, Database, Cloud, ServerCog, PenSquare,
  MousePointer2, MonitorSmartphone, Laptop, Chrome, Github, Wifi, LockKeyhole, KeyRound,
  Utensils, Scissors, Hammer, Gavel, Stethoscope,
  UserRoundSearch, Sun, Eye, Wrench,
  // Marketing & growth
  Search, MailOpen, Send, Target, Radar, Speaker, Film,
  BarChart2, PieChart, Activity, TrendingDown, MousePointerClick, Filter, Split,
  UserCheck, Share2, ThumbsUp, Music2, Headphones,
  MapPinned, Signal,
  // Capital & investment
  Presentation, FileBarChart, BarChart,
} from "lucide-react";

/**
 * Curated icon picker — maps every service name to a distinctive Lucide icon
 * by scanning for signature keywords (order matters, most specific first).
 * Falls back to the category default when no keyword matches.
 */

const CATEGORY_DEFAULT = {
  credit_financial:   CreditCard,
  start_business:     Briefcase,
  online_business:    ShoppingBag,
  start_brand:        Sparkles,
  nonprofit:          Heart,
  websites_tech:      Monitor,
  marketing_growth:   Megaphone,
  capital_investment: TrendingUp,
};

/** [regex, IconComponent] — first match wins. */
const RULES = [
  // ----- Credit & Finance signature icons -----
  [/\bfico\b/i,                 Calculator],
  [/\bvantagescore\b/i,         BarChart3],
  [/rapid[-\s]?rescore/i,       Zap],
  [/credit\s+freeze/i,          Lock],
  [/identity/i,                 ShieldCheck],
  [/credit\s+monitoring/i,      Radar],
  [/authorized\s+user|tradeline/i, Users2],
  [/collection/i,               ShieldAlert],
  [/charge[-\s]?off/i,          ShieldAlert],
  [/bankruptcy/i,               Scale],
  [/student\s+loan/i,           GraduationCap],
  [/medical\s+debt/i,           Stethoscope],
  [/judgment|public\s+record/i, Gavel],
  [/divorce/i,                  Handshake],
  [/cosigner/i,                 HandCoins],
  [/homebuyer|mortgage|first[-\s]?time\s+home/i, Home],
  [/auto\s+financ/i,            Car],
  [/utilization/i,              Percent],
  [/debt[-\s]?to[-\s]?income|dti/i, Calculator],
  [/personal\s+credit\s+review|credit\s+repair/i, FileText],
  [/credit[-\s]?building|building\s+plan/i, BarChart3],
  [/funding\s+readiness/i,      BadgeCheck],
  [/personal\s+funding/i,       Wallet],
  [/business\s+funding/i,       BadgeDollarSign],
  [/business\s+credit\s+card|card\s+stack/i, CreditCard],
  [/business\s+credit(\s+development|\s+profile)?/i, LineChart],
  [/financial\s+goal/i,         Target],
  [/emergency\s+fund/i,         PiggyBank],
  [/budget/i,                   Calculator],
  [/cash[-\s]?flow/i,           LineChart],
  [/tax[-\s]?season|tax\s+prep/i, Receipt],
  [/retirement/i,               Sun],
  [/financial\s+strategy/i,     LineChart],

  // ----- Business formation -----
  [/\bllc\b|business\s+formation/i, Building2],
  [/\bein\b/i,                  Hash],
  [/business\s+bank\s+account/i, Landmark],
  [/business\s+address|virtual\s+address/i, MapPin],
  [/business\s+phone|business\s+email/i, Contact],
  [/business\s+compliance|licenses|permits/i, ClipboardCheck],
  [/business\s+plan/i,          BookOpen],
  [/startup\s+strategy/i,       Rocket],
  [/buy\s+an\s+existing|business\s+acquisition|acquisition\s+funding/i, Handshake],
  [/s[-\s]?corp/i,              FileSignature],
  [/operating\s+agreement/i,    ScrollText],
  [/registered\s+agent/i,       Stamp],
  [/\bdba\b|fictitious/i,       Signature],
  [/trademark/i,                Copyright],
  [/insurance/i,                Umbrella],
  [/bookkeeping/i,              ClipboardList],
  [/payroll/i,                  DollarSign],
  [/business\s+tax|tax\s+strategy/i, Receipt],
  [/vendor\s+tradeline/i,       Warehouse],
  [/small\s+business\s+grant/i, Award],
  [/sba\s*7|sba\s*504|sba\s+loan|sba\s+express/i, Landmark],
  [/franchise/i,                Store],
  [/contractor|1099/i,          PenSquare],
  [/business\s+name/i,          Feather],
  [/contract\s+templates/i,     FileText],
  [/business\s+systems/i,       Cpu],
  [/advisory\s+board|business\s+advisory/i, Users],
  [/equity\s+split/i,           PieChart],
  [/post[-\s]?formation/i,      ClipboardCheck],

  // ----- Online business / e-commerce -----
  [/shopify\s+plus/i,           Rocket],
  [/shopify/i,                  ShoppingCart],
  [/amazon/i,                   Package],
  [/etsy/i,                     Palette],
  [/ebay/i,                     Store],
  [/walmart/i,                  ShoppingBag],
  [/tiktok\s+shop/i,            Music],
  [/tiktok/i,                   Music2],
  [/woocommerce|wordpress/i,    Layers],
  [/bigcommerce/i,              Boxes],
  [/print[-\s]?on[-\s]?demand/i, Shirt],
  [/dropship/i,                 Truck],
  [/digital\s+product|digital\s+course|course\s+platform/i, GraduationCap],
  [/subscription/i,             Repeat],
  [/marketplace/i,              Store],
  [/payment\s+processing|payment\s+portal/i, CreditCard],
  [/booking\s+system|scheduling\s+system/i, Bell],
  [/customer\s+portal/i,        UserRoundSearch],
  [/e[-\s]?commerce\s+growth|ecommerce\s+growth/i, TrendingUp],
  [/membership\s+site|membership\s+website/i, KeyRound],
  [/affiliate/i,                Share2],
  [/wholesale|\bb2b\b/i,        Warehouse],
  [/product\s+photography/i,    Camera],
  [/product\s+listing/i,        ScanBarcode],
  [/international\s+shipping/i, Globe2],
  [/\breturns\b/i,              RefreshCcw],
  [/customer\s+service/i,       MessagesSquare],
  [/abandoned\s+cart/i,         ShoppingCart],
  [/live\s+chat/i,              MessageCircle],
  [/post[-\s]?purchase\s+upsell/i, TrendingUp],
  [/loyalty|rewards\s+program/i, Star],
  [/wishlist/i,                 Bookmark],
  [/\breviews\b/i,              Star],
  [/age\s+verification/i,       Lock],

  // ----- Brand & design -----
  [/clothing\s+(line|brand)/i,  Shirt],
  [/product\s+brand/i,          Palette],
  [/logo|brand\s+identity/i,    Wand2],
  [/packaging/i,                Gift],
  [/product\s+sourcing/i,       Boxes],
  [/merchandise/i,              Gift],
  [/personal\s+brand/i,         UserPlus],
  [/brand\s+strategy/i,         Feather],
  [/influencer/i,               Users],
  [/tech\s+pack/i,              ScrollText],
  [/label|hangtag/i,            Ticket],
  [/brand\s+naming|brand\s+name/i, Feather],
  [/brand\s+voice|tone\s+guide/i, MessageSquare],
  [/brand\s+style\s+guide/i,    BookOpen],
  [/brand\s+photography/i,      Camera],
  [/brand\s+videography|videography/i, Video],
  [/brand\s+website/i,          Globe],
  [/look\s+book/i,              Image],
  [/product\s+sample/i,         Aperture],
  [/small[-\s]?batch|manufacturing/i, Warehouse],
  [/fulfillment/i,              Truck],
  [/brand\s+launch/i,           PartyPopper],
  [/press\s+release/i,          Newspaper],
  [/brand\s+partnership/i,      HandHeart],
  [/pop[-\s]?up\s+shop/i,       Store],
  [/wholesale\s+deck|retail\s+wholesale/i, ClipboardList],
  [/dtc\s+launch/i,             Rocket],
  [/barcode|gtin/i,             ScanBarcode],
  [/brand\s+ambassador/i,       Award],
  [/sustainability|esg/i,       Leaf],
  [/founder\s+story/i,          BookMarked],
  [/brand\s+community/i,        Users],
  [/rebranding|refresh/i,       RefreshCcw],

  // ----- Nonprofit -----
  [/501\(c\)|501c3|nonprofit\s+formation/i, HeartHandshake],
  [/nonprofit\s+website/i,      Globe],
  [/nonprofit\s+branding/i,     Wand2],
  [/donation\s+system/i,        HandHeart],
  [/fundraising/i,              HandCoins],
  [/grant[-\s]?readiness|grant\s+writing|grant\s+research|grant\s+report/i, Award],
  [/sponsorship/i,              Handshake],
  [/community\s+program/i,      HelpingHand],
  [/bylaws/i,                   ScrollText],
  [/board\s+development|board\s+of\s+directors/i, Users],
  [/volunteer/i,                UserPlus],
  [/annual\s+report/i,          FileBarChart],
  [/major\s+donor/i,            Trophy],
  [/monthly\s+giving/i,         Repeat],
  [/peer[-\s]?to[-\s]?peer/i,   Share2],
  [/nonprofit\s+crm/i,          Database],
  [/donor\s+journey/i,          Route],
  [/silent\s+auction/i,         Gavel],
  [/gala|event\s+planning/i,    PartyPopper],
  [/nonprofit\s+compliance/i,   ClipboardCheck],
  [/fiscal\s+sponsorship/i,     HeartHandshake],
  [/impact\s+measurement/i,     BarChart2],
  [/nonprofit\s+marketing/i,    Megaphone],
  [/church|faith[-\s]?based/i,  Church],
  [/pta|pto/i,                  School],
  [/foundation\s+setup/i,       Flag],
  [/community\s+foundation/i,   Group],
  [/nonprofit\s+merger/i,       HeartHandshake],
  [/public\s+charity/i,         Flag],

  // ----- Websites & Tech -----
  [/website\s+redesign/i,       RefreshCcw],
  [/new\s+business\s+website|marketing\s+site/i, Globe],
  [/e[-\s]?commerce\s+website/i, ShoppingCart],
  [/custom\s+client\s+portal|client\s+portal/i, KeyRound],
  [/mobile\s+app/i,             Smartphone],
  [/custom\s+software/i,        Code2],
  [/\bcrm\b/i,                  Database],
  [/ai\s+automation/i,          Bot],
  [/online\s+forms/i,           FileText],
  [/appointment\s+scheduling/i, Bell],
  [/website\s+maintenance/i,    Wrench],
  [/technology\s+strategy/i,    Cpu],
  [/landing\s+page/i,           MousePointerClick],
  [/sales\s+funnel\s+website/i, Filter],
  [/blog|seo\s+content/i,       PenSquare],
  [/learning\s+management|\blms\b/i, GraduationCap],
  [/podcast\s+website/i,        Headphones],
  [/portfolio\s+website/i,      Image],
  [/restaurant\s+website/i,     Utensils],
  [/real\s+estate\s+website/i,  Home],
  [/salon|spa/i,                Scissors],
  [/contractor|trades/i,        Hammer],
  [/law\s+firm/i,               Gavel],
  [/medical\s+practice/i,       Stethoscope],
  [/coaching\s+website|consulting\s+website/i, MessagesSquare],
  [/saas/i,                     Cloud],
  [/multi[-\s]?language/i,      Globe2],
  [/performance\s+audit/i,      Activity],
  [/security\s+audit/i,         LockKeyhole],
  [/accessibility\s+audit/i,    Eye],
  [/domain|dns/i,               Globe],
  [/email\s+hosting/i,          MailPlus],
  [/chatbot/i,                  Bot],

  // ----- Marketing & growth -----
  [/social\s+media/i,           Share2],
  [/content\s+creation|content\s+strategy/i, PenSquare],
  [/paid\s+advertising/i,       Target],
  [/search\s+engine\s+optimization|\bseo\b/i, Search],
  [/email\s+marketing/i,        MailOpen],
  [/sms\s+marketing/i,          MessageSquare],
  [/lead\s+generation/i,        Filter],
  [/sales\s+funnel/i,           Filter],
  [/reputation\s+management/i,  Star],
  [/google\s+business\s+profile/i, MapPinned],
  [/influencer\s+marketing/i,   Users],
  [/video\s+production/i,       Film],
  [/business\s+growth\s+strategy|growth\s+strategy/i, TrendingUp],
  [/brand\s+positioning/i,      Feather],
  [/copywriting/i,              PenLine],
  [/podcast\s+launch/i,         Headphones],
  [/youtube\s+ads|youtube\s+channel/i, Youtube],
  [/tiktok\s+growth|tiktok\s+ads/i, Music2],
  [/instagram\s+growth/i,       Instagram],
  [/linkedin/i,                 UserCheck],
  [/facebook\s+ads/i,           Facebook],
  [/google\s+ads/i,             Search],
  [/retargeting/i,              Target],
  [/marketing\s+automation/i,   Zap],
  [/conversion\s+rate|\bcro\b/i, Split],
  [/attribution|analytics/i,    BarChart2],
  [/referral\s+program/i,       Share2],
  [/partnership\s+marketing/i,  Handshake],
  [/marketing\s+team\s+coaching/i, MessagesSquare],
  [/fractional\s+cmo/i,         Award],
  [/marketing\s+playbook/i,     BookOpen],
  [/digital\s+marketing/i,      Megaphone],

  // ----- Capital & investment -----
  [/capital[-\s]?raising|expansion\s+capital/i, Rocket],
  [/investor[-\s]?readiness/i,  BadgeCheck],
  [/business\s+valuation/i,     Calculator],
  [/pitch\s+deck/i,             Presentation],
  [/financial\s+projection/i,   LineChart],
  [/real\s+estate\s+investment|real\s+estate\s+deal|syndication/i, Home],
  [/business\s+investment/i,    BarChart],
  [/angel\s+investor/i,         Sparkles],
  [/vc\s+outreach|venture\s+debt|venture/i, Rocket],
  [/family\s+office/i,          Building],
  [/crowdfunding/i,             Users],
  [/equipment\s+financing/i,    Warehouse],
  [/revenue[-\s]?based/i,       Percent],
  [/mezzanine/i,                Layers],
  [/data\s+room/i,              Database],
  [/cap\s+table/i,              PieChart],
  [/safe\s+note/i,              FileSignature],
  [/convertible\s+note/i,       ScrollText],
  [/priced\s+round/i,           HandCoins],
  [/fund\s+of\s+funds/i,        Coins],
  [/hard\s+money/i,             Coins],
  [/\bdscr\b/i,                 Home],
  [/business\s+line\s+of\s+credit|\bloc\b\s+strategy/i, CreditCard],
  [/merchant\s+cash\s+advance|\bmca\b/i, ShieldAlert],
  [/investor\s+update/i,        Newspaper],
  [/exit\s+strategy/i,          Rocket],
];

/**
 * Given a service ({name, category_id}), return a curated Lucide icon.
 */
export function serviceIconFor(service) {
  const fallback = CATEGORY_DEFAULT[service?.category_id] || Sparkles;
  const name = (service?.name || "").toString();
  if (!name) return fallback;
  for (const [re, Icon] of RULES) {
    if (re.test(name)) return Icon || fallback;
  }
  return fallback;
}

export const CATEGORY_ICON = CATEGORY_DEFAULT;
